import { useEffect, useMemo, useRef, useState } from 'react';

import {
  canPerformTableAction,
  FormElementKind,
  FormElementTableColumnKind,
  tableAction,
  TableActionType,
  TableFormElementAnswerSchema,
  TableFormElement as TableFormElementType,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { capitalize } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import ReactSelect from 'react-select';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { ArrowDownIcon } from '@/components/icons/arrow-down';
import { ArrowUpIcon } from '@/components/icons/arrow-up';
import { CloneIcon } from '@/components/icons/clone';
import { CloseIcon } from '@/components/icons/close';
import { DeleteIcon } from '@/components/icons/delete';
import { EllipsisHorizontalIcon } from '@/components/icons/ellipsis-horizontal';
import { EraseIcon } from '@/components/icons/erase';
import { GripDotVerticalIcon } from '@/components/icons/grip-dot-vertical';
import PlusIcon from '@/components/icons/plus';
import { WarningTriangleIcon } from '@/components/icons/warning-triangle';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SELECT_COMPONENTS, SELECT_STYLES } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { getListsQueryOptions } from '@/queries/lists';

import { TableColumnConfigLabel, TableConfigLabels } from '../config-field-labels';
import { ConfigLabel } from '../config-label';
import { FieldShell } from '../field-shell';
import { DateFormElement } from './date';
import { DropdownFormElement } from './dropdown/dropdown';
import { NumberFormElement } from './number';
import { SingleLineInputFormElement } from './single-line-input';

type TableValue = z.infer<typeof TableFormElementAnswerSchema>['value'];

const columnHelper = createColumnHelper<TableValue[number]>();

const ACTIONS_COLUMN_ID = '__actions';

interface FormValues {
  [key: string]: TableValue;
}

type TableColumnState = {
  id: string;
  column: TableFormElementType['config']['columns'][number] & { id: string };
};

type ConfirmState = {
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
};

export const TableFormElement = ({
  element,
  readonly,
  isPreview = false,
  hideLabel,
}: {
  element: TableFormElementType;
  readonly?: boolean;
  isPreview?: boolean;
  hideLabel?: boolean;
}) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();
  const formRows = watch(element.id);

  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

  // Structural actions replace the whole answer array via setValue; bumping this
  // generation re-keys every row so the cell Controllers remount and read the
  // reordered/cleared values — a bare setValue leaves mounted inputs stale.
  const generation = useRef(0);

  const rows = useMemo(() => {
    return Array.isArray(formRows) ? [...formRows] : [];
  }, [formRows]);

  // A table first revealed by conditional logic has no stored answer and would render with zero rows. Seed
  // one blank row via `initializeTableInitialValues` (the same helper the fill pages use) so it matches an
  // always-visible table. Seed only when the value is absent (`undefined`): `[]` is a deliberate "all rows
  // deleted" state, and only in fill mode (a readonly record with no rows captured none).
  useEffect(() => {
    if (!readonly && !Array.isArray(formRows)) {
      setValue(element.id, [[...initializeTableInitialValues(element)]]);
    }
  }, [readonly, formRows, element, setValue]);

  const dataColumns = useMemo(
    () =>
      element.config.columns.map((col, index) => {
        return columnHelper.accessor((row) => row[index].kind, {
          id: index.toString(),
          header: col.label,
          cell: ({ row: tableRow }) => {
            const rowIndex = tableRow.index;

            return match(col)
              .with({ kind: FormElementTableColumnKind.SingleLineInput }, () => (
                <Controller
                  control={control}
                  name={`${element.id}[${rowIndex}][${index}].value`}
                  render={({ field: { value, onChange } }) => (
                    <SingleLineInputFormElement
                      element={{ id: `${index}`, kind: FormElementKind.SingleLineInput, label: '', required: false, config: undefined }}
                      value={value}
                      onChange={(newValue) => onChange(newValue)}
                      readonly={readonly}
                    />
                  )}
                />
              ))
              .with({ kind: FormElementTableColumnKind.Number }, () => (
                <Controller
                  control={control}
                  name={`${element.id}[${rowIndex}][${index}].value`}
                  render={({ field: { value, onChange } }) => (
                    <NumberFormElement
                      element={{
                        id: `${index}`,
                        kind: FormElementKind.Number,
                        label: '',
                        required: false,
                        config: {
                          allowNegative: false,
                          allowDecimal: true,
                          commaSeparator: true,
                        },
                      }}
                      value={value}
                      onChange={onChange}
                      readonly={readonly}
                    />
                  )}
                />
              ))
              .with({ kind: FormElementTableColumnKind.Dropdown }, (col) => (
                <Controller
                  control={control}
                  name={`${element.id}[${rowIndex}][${index}].value`}
                  render={({ field: { value, onChange } }) => (
                    <DropdownFormElement
                      element={{
                        id: `${element.id}-${rowIndex}-${index}`,
                        kind: FormElementKind.Dropdown,
                        label: '',
                        required: false,
                        config:
                          col.config?.source === 'list'
                            ? {
                                source: 'list',
                                listId: col.config.listId ?? '',
                                multiple: col.config.multiple ?? false,
                              }
                            : {
                                source: 'static',
                                options: col.config?.options ?? [],
                                multiple: col.config?.multiple ?? false,
                              },
                      }}
                      value={value}
                      onChange={onChange}
                      readonly={readonly}
                    />
                  )}
                />
              ))
              .with({ kind: FormElementTableColumnKind.Date }, () => (
                <Controller
                  control={control}
                  name={`${element.id}[${rowIndex}][${index}].value`}
                  render={({ field: { value, onChange } }) => (
                    <DateFormElement
                      element={{
                        id: `${index}`,
                        kind: FormElementKind.Date,
                        label: '',
                        required: false,
                        config: { showDate: true, showTime: false, isSubmissionDate: false },
                      }}
                      value={value}
                      onChange={onChange}
                      readonly={readonly}
                    />
                  )}
                />
              ))
              .with({ kind: FormElementTableColumnKind.Time }, () => (
                <Controller
                  control={control}
                  name={`${element.id}[${rowIndex}][${index}].value`}
                  render={({ field: { value, onChange } }) => (
                    <DateFormElement
                      element={{
                        id: `${index}`,
                        kind: FormElementKind.Date,
                        label: '',
                        required: false,
                        config: { showDate: false, showTime: true, isSubmissionDate: false },
                      }}
                      value={value}
                      onChange={onChange}
                      readonly={readonly}
                    />
                  )}
                />
              ))
              .exhaustive();
          },
        });
      }),
    [element.config.columns, element.id, control, readonly],
  );

  const updateRows = (next: TableValue) => setValue(element.id, next, { shouldDirty: true });

  const runAction = (action: TableActionType, rowIndex?: number) => {
    generation.current += 1;
    updateRows(tableAction(action, { rows, columns: element.config.columns, rowIndex }));
  };

  const confirmAction = (state: Omit<ConfirmState, 'onConfirm'>, action: TableActionType, rowIndex?: number) =>
    setConfirmState({ ...state, onConfirm: () => runAction(action, rowIndex) });

  const renderRowActions = (rowIndex: number) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Row ${rowIndex + 1} actions`}
          className="text-secondary-text hover:text-default-text flex items-center justify-center rounded p-1">
          <EllipsisHorizontalIcon />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem
          disabled={!canPerformTableAction(TableActionType.MoveRowUp, { rows, rowIndex })}
          onSelect={() => runAction(TableActionType.MoveRowUp, rowIndex)}>
          <ArrowUpIcon className="mr-2" size={16} />
          Move up
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canPerformTableAction(TableActionType.MoveRowDown, { rows, rowIndex })}
          onSelect={() => runAction(TableActionType.MoveRowDown, rowIndex)}>
          <ArrowDownIcon className="mr-2" size={16} />
          Move down
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => runAction(TableActionType.InsertRowAbove, rowIndex)}>
          <PlusIcon className="mr-2" size={16} />
          Insert row above
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => runAction(TableActionType.InsertRowBelow, rowIndex)}>
          <PlusIcon className="mr-2" size={16} />
          Insert row below
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => runAction(TableActionType.CloneRow, rowIndex)}>
          <CloneIcon className="mr-2" size={16} />
          Clone row
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!canPerformTableAction(TableActionType.ClearRow, { rows, rowIndex })}
          onSelect={() =>
            confirmAction(
              { title: 'Clear this row?', description: 'Are you sure you want to clear this row?', confirmLabel: 'Clear' },
              TableActionType.ClearRow,
              rowIndex,
            )
          }>
          <EraseIcon className="mr-2" size={16} />
          Clear row
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canPerformTableAction(TableActionType.DeleteRow, { rows, rowIndex })}
          className="text-default-red focus:text-default-red"
          onSelect={() =>
            confirmAction(
              { title: 'Delete this row?', description: 'Are you sure you want to delete this row?', confirmLabel: 'Delete' },
              TableActionType.DeleteRow,
              rowIndex,
            )
          }>
          <DeleteIcon className="mr-2" size={16} />
          Delete row
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderTableActions = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Table actions" className="text-secondary-text hover:text-default-text flex items-center justify-center rounded p-1">
          <EllipsisHorizontalIcon />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem
          disabled={!canPerformTableAction(TableActionType.ClearAllRows, { rows })}
          onSelect={() =>
            confirmAction(
              { title: 'Clear all rows?', description: 'Are you sure you want to clear ALL rows from this table?', confirmLabel: 'Clear all' },
              TableActionType.ClearAllRows,
            )
          }>
          <EraseIcon className="mr-2" size={16} />
          Clear all rows
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canPerformTableAction(TableActionType.DeleteAllRows, { rows })}
          className="text-default-red focus:text-default-red"
          onSelect={() =>
            confirmAction(
              { title: 'Delete all rows?', description: 'Are you sure you want to delete ALL rows from this table?', confirmLabel: 'Delete all' },
              TableActionType.DeleteAllRows,
            )
          }>
          <DeleteIcon className="mr-2" size={16} />
          Delete all rows
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const actionColumn = columnHelper.display({
    id: ACTIONS_COLUMN_ID,
    header: () => renderTableActions(),
    cell: ({ row }) => renderRowActions(row.index),
  });

  const columns = readonly || isPreview ? dataColumns : [actionColumn, ...dataColumns];

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <FieldShell
        label={element.label}
        required={element.required}
        hideLabel={hideLabel}
        error={errors[element.id] ? 'At least one row is required' : undefined}>
        <div className="space-y-2">
          <div className="border-grey-3 overflow-auto rounded-md border">
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  {table.getFlatHeaders().map((header) => (
                    <TableHead key={header.id} className={cn('border-0 border-r border-b last:border-r-0', header.column.id === ACTIONS_COLUMN_ID && 'w-12')}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody className="[&_tr:last-child_td]:border-b-0">
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={`gen-${generation.current}-${row.id}`}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          'border-0 border-r border-b last:border-r-0',
                          cell.column.id === ACTIONS_COLUMN_ID ? 'w-12 align-middle' : 'relative min-w-[200px] [&_.space-y-1>*]:mb-0',
                        )}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {!readonly && (
            <Button
              type="button"
              variant="default"
              disabled={isPreview}
              onClick={() => runAction(TableActionType.AddRow)}
              className={cn('w-full', isPreview && 'opacity-70')}>
              <PlusIcon className="mr-1" size={16} />
              Add Row
            </Button>
          )}
        </div>
      </FieldShell>

      <ConfirmDialog
        open={!!confirmState}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmState(null);
          }
        }}
        title={confirmState?.title ?? ''}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel ?? 'Confirm'}
        confirmVariant="destructive"
        onConfirm={() => confirmState?.onConfirm()}
      />
    </>
  );
};

export function TableFormElementConfig({ element, onChange }: { element: TableFormElementType; onChange?: (updates: Partial<TableFormElementType>) => void }) {
  const [columns, setColumns] = useState<TableColumnState[]>(
    element.config.columns.map((col) => ({
      id: col.id || crypto.randomUUID(),
      column: { ...col, id: col.id || crypto.randomUUID() },
    })),
  );

  const { data: lists } = useQuery(getListsQueryOptions());
  const isLastColumn = columns.length <= 1;

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newColumns = [...columns];
    const [movedItem] = newColumns.splice(result.source.index, 1);
    newColumns.splice(result.destination.index, 0, movedItem);
    setColumns(newColumns);

    onChange?.({
      config: {
        ...element.config,
        columns: newColumns.map((col) => col.column),
      },
    });
  };

  const handleAddColumn = () => {
    const newColumn: TableColumnState = {
      id: crypto.randomUUID(),
      column: {
        id: crypto.randomUUID(),
        kind: FormElementTableColumnKind.SingleLineInput,
        label: 'New Column',
        config: undefined,
      },
    };

    const newColumns = [...columns, newColumn];
    setColumns(newColumns);
    onChange?.({
      config: {
        ...element.config,
        columns: newColumns.map((col) => col.column),
      },
    });
  };

  const handleRemoveColumn = (columnToRemove: TableColumnState) => {
    if (isLastColumn) return;

    const newColumns = columns.filter((col) => col.id !== columnToRemove.id);
    setColumns(newColumns);
    onChange?.({
      config: {
        ...element.config,
        columns: newColumns.map((col) => col.column),
      },
    });
  };

  const handleColumnChange = (columnId: string, updates: Partial<TableColumnState['column']>) => {
    const newColumns = columns.map((col) => {
      if (col.id !== columnId) return col;

      const kind = updates.kind ?? col.column.kind;
      const existingConfig = col.column.config;

      let config;
      if (kind === FormElementTableColumnKind.Number) {
        config = {
          allowDecimal: true,
          allowNegative: false,
          commaSeparator: true,
        };
      } else if (kind === FormElementTableColumnKind.Dropdown) {
        const dropdownConfig = existingConfig as { source: 'static' | 'list'; options?: string[]; multiple?: boolean; listId?: string } | undefined;
        config = {
          source: dropdownConfig?.source ?? 'static',
          options: dropdownConfig?.source === 'static' ? (dropdownConfig.options ?? []) : [],
          multiple: dropdownConfig?.multiple ?? false,
          listId: dropdownConfig?.source === 'list' ? (dropdownConfig.listId ?? '') : '',
          ...updates.config,
        };
      } else if (kind === FormElementTableColumnKind.Date || kind === FormElementTableColumnKind.Time) {
        config = {
          showDate: true,
          showTime: true,
        };
      }

      const newColumn = {
        ...col.column,
        ...updates,
        kind,
        config,
      } as TableFormElementType['config']['columns'][number] & { id: string };

      return {
        ...col,
        column: newColumn,
      };
    });

    setColumns(newColumns);
    onChange?.({
      config: {
        ...element.config,
        columns: newColumns.map((col) => col.column),
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">{TableConfigLabels.columns}:</Label>
          <Button type="button" onClick={handleAddColumn} variant="success" size="sm">
            <PlusIcon className="mr-1" size={16} />
            Add Column
          </Button>
        </div>

        {columns.length === 0 && (
          <div className="text-default-red flex items-center gap-2 text-sm">
            <WarningTriangleIcon className="h-4 w-4" />
            <span>This table needs at least one column</span>
          </div>
        )}

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="columns">
            {(provided) => (
              <ul ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                {columns.map((col, index) => (
                  <Draggable key={col.id} draggableId={col.id} index={index}>
                    {(provided) => (
                      <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="border-grey-3 flex cursor-move items-center gap-2 rounded-md border p-2">
                        <div {...provided.dragHandleProps}>
                          <GripDotVerticalIcon className="text-secondary-text" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-1 items-center gap-2">
                            <Input
                              value={col.column.label}
                              onChange={(e) => handleColumnChange(col.id, { label: e.target.value })}
                              placeholder="Column name"
                              className="flex-1"
                            />
                            <ReactSelect
                              classNames={SELECT_STYLES<{ label: string; value: FormElementTableColumnKind }>()}
                              components={{
                                ...SELECT_COMPONENTS<{ label: string; value: FormElementTableColumnKind }>(),
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              unstyled
                              value={{
                                label: TableColumnConfigLabel[col.column.kind] ?? capitalize(col.column.kind),
                                value: col.column.kind,
                              }}
                              options={Object.values(FormElementTableColumnKind).map((kind) => ({
                                label: TableColumnConfigLabel[kind],
                                value: kind,
                              }))}
                              onChange={(selected) => selected && handleColumnChange(col.id, { kind: selected.value })}
                              className="w-40"
                            />
                          </div>

                          {match(col.column)
                            .with({ kind: FormElementTableColumnKind.Dropdown }, (dropdownCol) => (
                              <div className="flex flex-col items-center gap-2">
                                <ReactSelect
                                  classNames={SELECT_STYLES<{ label: string; value: 'static' | 'list' }>()}
                                  components={{
                                    ...SELECT_COMPONENTS<{ label: string; value: 'static' | 'list' }>(),
                                  }}
                                  styles={{
                                    container: (base) => ({ ...base, width: '100%' }),
                                  }}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  unstyled
                                  value={{
                                    label: dropdownCol.config?.source === 'list' ? 'List' : 'Static',
                                    value: dropdownCol.config?.source ?? 'static',
                                  }}
                                  options={[
                                    { label: 'Static', value: 'static' },
                                    { label: 'List', value: 'list' },
                                  ]}
                                  onChange={(selected) => {
                                    if (!selected) return;
                                    const value = selected.value;
                                    if (value === 'list') {
                                      handleColumnChange(col.id, {
                                        config: {
                                          source: 'list' as const,
                                          listId: lists?.[0]?.id ?? '',
                                          multiple: false,
                                        },
                                      });
                                    } else {
                                      handleColumnChange(col.id, {
                                        config: {
                                          source: 'static' as const,
                                          options: [],
                                          multiple: false,
                                        },
                                      });
                                    }
                                  }}
                                  className="w-24"
                                />
                                {dropdownCol.config?.source === 'list' && lists && (
                                  <ReactSelect
                                    classNames={SELECT_STYLES<{ label: string; value: string }>()}
                                    components={{
                                      ...SELECT_COMPONENTS<{ label: string; value: string }>(),
                                    }}
                                    styles={{
                                      container: (base) => ({ ...base, width: '100%' }),
                                    }}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    unstyled
                                    options={lists.map((list: { id: string; name: string }) => ({ label: list.name, value: list.id }))}
                                    value={
                                      lists.find((list: { id: string; name: string }) => list.id === (dropdownCol.config as { listId: string })?.listId)
                                        ? {
                                            label: lists.find(
                                              (list: { id: string; name: string }) => list.id === (dropdownCol.config as { listId: string })?.listId,
                                            )!.name,
                                            value: (dropdownCol.config as { listId: string })?.listId ?? '',
                                          }
                                        : null
                                    }
                                    onChange={(selected) =>
                                      handleColumnChange(col.id, {
                                        config: {
                                          source: 'list' as const,
                                          listId: selected?.value ?? '',
                                          multiple: (dropdownCol.config as { multiple: boolean })?.multiple ?? false,
                                        },
                                      })
                                    }
                                    placeholder="Select list..."
                                    className="w-40"
                                  />
                                )}
                                {dropdownCol.config?.source === 'static' && (
                                  <div className="flex w-full flex-col gap-2">
                                    <div className="flex gap-2">
                                      <Input
                                        placeholder="Add list item..."
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const newValue = e.currentTarget.value;
                                            if (!newValue) return;

                                            const currentOptions = dropdownCol.config?.source === 'static' ? dropdownCol.config.options : [];
                                            handleColumnChange(col.id, {
                                              config: {
                                                source: 'static' as const,
                                                options: [...currentOptions, newValue],
                                                multiple: dropdownCol.config?.multiple ?? false,
                                              },
                                            });
                                            e.currentTarget.value = '';
                                          }
                                        }}
                                      />
                                      <Button
                                        type="button"
                                        variant="outline"
                                        onClick={(e) => {
                                          const input = e.currentTarget.parentElement?.querySelector('input');
                                          if (!input) return;

                                          const newValue = input.value;
                                          if (!newValue) return;

                                          const currentOptions = dropdownCol.config?.source === 'static' ? dropdownCol.config.options : [];
                                          handleColumnChange(col.id, {
                                            config: {
                                              source: 'static' as const,
                                              options: [...currentOptions, newValue],
                                              multiple: dropdownCol.config?.multiple ?? false,
                                            },
                                          });
                                          input.value = '';
                                        }}>
                                        Add
                                      </Button>
                                    </div>
                                    <ul className="space-y-1">
                                      {(dropdownCol.config?.source === 'static' ? dropdownCol.config.options : []).map(
                                        (option: string, optionIndex: number) => (
                                          <li key={optionIndex}>
                                            <div className="border-grey-3 flex items-center gap-2 rounded-md border p-1 text-sm">
                                              <span className="flex-1">{option}</span>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                aria-label="Remove option"
                                                onClick={() => {
                                                  if (dropdownCol.config?.source !== 'static') return;
                                                  const currentOptions = [...dropdownCol.config.options];
                                                  currentOptions.splice(optionIndex, 1);
                                                  handleColumnChange(col.id, {
                                                    config: {
                                                      source: 'static' as const,
                                                      options: currentOptions,
                                                      multiple: dropdownCol.config?.multiple ?? false,
                                                    },
                                                  });
                                                }}>
                                                <CloseIcon className="text-default-red size-4" />
                                              </Button>
                                            </div>
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            ))
                            .otherwise(() => (
                              <></>
                            ))}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label="Remove column"
                          disabled={isLastColumn}
                          onClick={() => handleRemoveColumn(col)}>
                          <CloseIcon className="text-default-red size-4" />
                        </Button>
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
}

export const initializeTableInitialValues = (element: TableFormElementType): TableValue[number] =>
  element.config.columns.map((col) =>
    match(col.kind as FormElementTableColumnKind)
      .with(FormElementTableColumnKind.SingleLineInput, (kind) => ({ kind, value: '' }))
      .with(FormElementTableColumnKind.Number, (kind) => ({ kind, value: 0 }))
      .with(FormElementTableColumnKind.Date, (kind) => ({ kind, value: '' }))
      .with(FormElementTableColumnKind.Time, (kind) => ({ kind, value: '' }))
      .with(FormElementTableColumnKind.Dropdown, (kind) => ({ kind, value: null }))
      .exhaustive(),
  );
